/**
 * Auth Context
 * Manages authentication state and provides auth utilities
 */

import { type ReactNode, createContext, useCallback, useContext, useEffect, useState } from "react"
import { apiClient } from "../lib/api"
import type { AdminUser } from "../types"

interface AuthContextType {
  user: AdminUser | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AdminUser | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const checkAuth = useCallback(async () => {
    try {
      const userData = await apiClient.getMe()
      setUser(userData as AdminUser)
    } catch (error) {
      console.error("Auth check failed:", error)
      setUser(null)
      apiClient.clearToken()
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    // Check if user is authenticated on mount
    const token = localStorage.getItem("admin_token")
    if (token) {
      checkAuth()
    } else {
      setIsLoading(false)
    }
  }, [checkAuth])

  const login = async (email: string, password: string) => {
    setIsLoading(true)
    try {
      const session = await apiClient.login(email, password)
      setUser(session.adminUser)
    } finally {
      setIsLoading(false)
    }
  }

  const logout = async () => {
    setIsLoading(true)
    try {
      await apiClient.logout()
    } catch (error) {
      console.error("Logout failed:", error)
    } finally {
      setUser(null)
      setIsLoading(false)
    }
  }

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    logout,
    checkAuth,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider")
  }
  return context
}
